import { useMemo, useState } from 'react';
import { get } from 'lodash';
import { Col, Row, Space } from 'antd';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { AISMessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ProgressChecklistItem } from 'components/new-ui/composed/progress-checklist/types';
import ValidationProgressSidebar from './ValidationProgressSidebar';
import IrelandH1QuickFixTable from './ireland/IrelandH1QuickFixTable';
import FixItemModal from './fix-item-modal/FixItemModal';
import { getFieldChecklist } from './fix-item-modal/itemFieldsRegistry';
import { Sidebar } from '../styles';
import { ItemErrors } from '../types';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import PackageIcon from 'components/icons/PackageIcon';
import { StyledFormSectionWrapper } from 'components/new-ui/composed/multistep-form/styles';

type Props = {
    // Note: these describe the *source* declaration the items/errors came from - the errors
    // table shown depends on the source's shape, not the mirrored declaration being generated.
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    messageType: MessageTypes;
    items: any[];
    errors: Record<number, ItemErrors>;
    // The errors as of when the page loaded - see FixItemModal's `initialItemErrors`.
    initialErrors: Record<number, ItemErrors>;
    onResolvedChange: (resolved: boolean) => void;
    onItemFixed: (index: number, rawItem: any) => void | Promise<void>;
    // Base URL of the source declaration - see FixItemModal's `sourceDeclarationUrl`.
    sourceDeclarationUrl?: string;
};

const IssuesView = ({
    country,
    internalType,
    messageType,
    items,
    errors,
    initialErrors,
    onResolvedChange,
    onItemFixed,
    sourceDeclarationUrl,
}: Props) => {
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>(undefined);

    const openFixItemModal = selectedItemIndex !== undefined;

    // Note: For now we only support these source declaration types. Extend the mapping when needed.
    const itemErrorsTable = useMemo(() => {
        if (
            country === DeclarationCountry.IRELAND &&
            internalType === DeclarationInternalType.IMPORT &&
            messageType === AISMessageTypes.H1
        ) {
            return (
                <IrelandH1QuickFixTable
                    data={items}
                    errors={errors}
                    onQuickFix={(_, index) => setSelectedItemIndex(index)}
                />
            );
        }

        return null;
    }, [country, internalType, messageType, items, errors]);

    const fieldChecklist = useMemo(
        () => getFieldChecklist(country, internalType, messageType),
        [country, internalType, messageType]
    );

    // One row per field. A field is "passed" only if every item has a valid value for it.
    const progressChecklistItems: ProgressChecklistItem[] = useMemo(
        () =>
            fieldChecklist.map(({ path, label }) => ({
                key: path,
                label,
                group: 'Goods Item Level',
                passed: !items.some((_, index) => get(errors[index], path)),
                errorMessage: undefined,
            })),
        [fieldChecklist, items, errors]
    );

    return (
        <>
            <Row gutter={[15, 24]}>
                <Col flex="auto">
                    <StyledFormSectionWrapper style={{ maxWidth: 'fit-content' }}>
                        <FormSectionHeader title="Item Level" icon={<PackageIcon />} />
                        <Space direction="vertical" size={16} style={{ marginTop: 24 }}>
                            <div>{itemErrorsTable}</div>
                        </Space>
                    </StyledFormSectionWrapper>
                </Col>
                <Col flex="none">
                    <Sidebar>
                        <ValidationProgressSidebar items={progressChecklistItems} />
                    </Sidebar>
                </Col>
            </Row>

            <FixItemModal
                open={openFixItemModal}
                country={country}
                internalType={internalType}
                messageType={messageType}
                sourceDeclarationUrl={sourceDeclarationUrl}
                onClose={() => setSelectedItemIndex(undefined)}
                onPrevItem={() => setSelectedItemIndex((index) => (index ? index - 1 : index))}
                onNextItem={() =>
                    setSelectedItemIndex((index) =>
                        index !== undefined && index < items.length - 1 ? index + 1 : index
                    )
                }
                item={selectedItemIndex !== undefined ? items[selectedItemIndex] : undefined}
                itemNumber={selectedItemIndex ?? 0}
                totalItems={items.length}
                itemErrors={selectedItemIndex !== undefined ? errors[selectedItemIndex] : undefined}
                initialItemErrors={selectedItemIndex !== undefined ? initialErrors[selectedItemIndex] : undefined}
                onSave={(rawItem) => {
                    if (selectedItemIndex !== undefined) return onItemFixed(selectedItemIndex, rawItem);
                }}
            />
        </>
    );
};

export default IssuesView;
